/* eslint-disable sonarjs/cognitive-complexity */
import { DropdownItems, FilterField } from '@react/Filterbar/Filters/FiltersTypes'
import { isAuthorMode } from '@site/js/utils/author-mode'

import { LAST_ACTIVE_FILTER } from './constants'

// Get filters from array1 not in array2
function getFilterDifference(array1: FilterField[] & DropdownItems[], array2: FilterField[] & DropdownItems[]): FilterField[] & DropdownItems[] {
  return array1?.filter(object1 => !array2?.some(object2 => object1.key === object2.key)) || []
}

function sortArrayOfObjectsByStringKey(arr: FilterField[] & DropdownItems[], key: string): FilterField[] & DropdownItems[] {
  // Check all items for year/month keys, not just arr[0]
  const hasYearKeys = arr.length > 0 && arr.every(item => /^\d{4}$/.test(item.key));
  const hasMonthKeys = arr.length > 0 && arr.every(item => /^\d{4}-\d{2}$/.test(item.key));

  if (hasYearKeys) {
    return arr.sort((a, b) => (b.key || '').localeCompare(a.key || '')); // Descending
  }
  if (hasMonthKeys) {
    return arr.sort((a, b) => (a.key || '').localeCompare(b.key || '')); // Ascending
  }
  return arr.sort((a, b) => {
    const aVal = a[key]?.toLowerCase() || '';
    const bVal = b[key]?.toLowerCase() || '';
    return aVal.localeCompare(bVal);
  });
}

const getFilters = ({
  currentData = [],
  newData = [],
  filterType,
  filterKey,
  level,
  sessionFilterFieldsKey,
}: {
  currentData: FilterField[] & DropdownItems[]
  newData: FilterField[] & DropdownItems[]
  filterType?: string
  filterKey?: string
  level: number
  sessionFilterFieldsKey: string
}): FilterField[] & DropdownItems[] => {
  // Load latest option user selected, so we do not disable this filter
  const lastActiveFilter = getItemFromSession(LAST_ACTIVE_FILTER)

  // Handle new filters from newData only. Eg. newData.length > currentData.length
  const diff = getFilterDifference(newData, currentData)

  const filters = currentData.reduce((acc, filter) => {
    const filterKeyToIgnore = filterKey || filter.key
    const found = newData.find(function (foundFilter) {
      return foundFilter.key === filter.key
    })

    if (found) {
      return [
        ...acc,
        {
          ...found,
          disabled: found.items?.length <= 0,
          ...(found.type !== 'text'
            ? {
                items: getFilters({
                  currentData: filter.items,
                  newData: found.items,
                  filterKey: filterKeyToIgnore,
                  filterType,
                  level: 1,
                  sessionFilterFieldsKey,
                }),
              }
            : {}),
          ...((found as DropdownItems).children?.length > 0
            ? {
                children: getFilters({
                  currentData: (filter as DropdownItems).children,
                  newData: (found as DropdownItems).children,
                  filterKey: filterKeyToIgnore,
                  filterType,
                  level: 2,
                  sessionFilterFieldsKey,
                }),
              }
            : {}),
          ...((found as FilterField).subFilters?.length > 0
            ? {
                subFilters: getFilters({
                  currentData: (filter as FilterField).subFilters,
                  newData: (found as FilterField).subFilters,
                  filterKey: filterKeyToIgnore,
                  filterType,
                  level: 2,
                  sessionFilterFieldsKey,
                }),
              }
            : {}),
        },
      ]
    }

    // Not found, copy previous data, disable if not last selected filter OR keep previous disabled status
    return [
      ...acc,
      {
        ...filter,
        disabled: filterKeyToIgnore !== lastActiveFilter || filter.disabled,
      },
    ]
  }, [])

  // At parent level we want sorted by label and at items/children level we want sorted by value
  const sortedFilters = level === 0
    ? [...filters, ...diff]
    : sortArrayOfObjectsByStringKey([...filters, ...diff], 'value');
  !isAuthorMode() && sessionStorage.setItem(sessionFilterFieldsKey, JSON.stringify(sortedFilters))

  return sortedFilters
}

export const mergeAndUpdate = ({
  currentData,
  newData,
  filterType,
  filterKey,
  sessionFilterFieldsKey,
}: {
  currentData: FilterField[]
  newData: FilterField[]
  filterType?: string
  filterKey?: string
  sessionFilterFieldsKey?: string
}): FilterField[] => {
  // Nothing back, we do not display
  if (!newData) {
    return []
  }

  // Initial state (currentData) is empty, get inital data (newData) on page load
  if (!currentData) {
    const newDataArray = newData.map(e => ({
      ...e,
      disabled: e.items?.length <= 0,
      // Sort items (level 1: year keys descending, month keys ascending, others alphabetical)
      ...(e.items?.length > 0 ? { items: sortArrayOfObjectsByStringKey([...e.items], 'value') } : {}),
    }))
    !isAuthorMode() && sessionStorage.setItem(sessionFilterFieldsKey, JSON.stringify(newDataArray))
    return newDataArray
  }

  if (filterType === 'quickfilter') {
    return currentData
  }

  return getFilters({ currentData, newData, filterType, filterKey, level: 0, sessionFilterFieldsKey })
}

export const getItemFromSession = (sessionId: string) => {
  const sessionStorageValue = sessionStorage.getItem(sessionId)
  return JSON.parse(sessionStorageValue && sessionStorageValue !== 'undefined' ? sessionStorageValue : '""')
}

export const setFiltersFromQueryParams = ({ queryParams = [], selectedFilters = [], prefix = '', setOffsetState, setCalculatedOffsetState }) => {
  queryParams?.forEach(param => {
    const filterParam = param.split('=')
    if (filterParam[0].includes(prefix)) {
      const key = filterParam[0].substring(prefix.length)
      const value = filterParam[1].split(',')
      selectedFilters.push({ key, value })

      switch (key) {
        case 'offset':
          setOffsetState([value])
          break
        case 'calculatedOffset':
          setCalculatedOffsetState([value])
          break
        default:
          break
      }
    }
  })
}

export const arrayFromSearchQueryParams = (): string[] => {
  const queryParams = new URLSearchParams(window.location.search)
  return queryParams.size ? decodeURIComponent(queryParams.toString()).replace(/\+/g, ' ').split('&') : []
}

export const getSearchQueryParams = (prefix: string, current = true) =>
  uniqueArrayElements(
    arrayFromSearchQueryParams()?.filter(queryParam => {
      const key = queryParam.split('=')
      return current ? key[0].includes(prefix) : !key[0].includes(prefix)
    }),
  )

export const multipleSelectionHandler = (prefix: string, current = true) => {
  return getSearchQueryParams(prefix, current)?.map(queryParam => {
    const multipleSelected = []
    const params = queryParam.split('=')
    const values = params[1].split(',')
    values.forEach(value => multipleSelected.push(`${params[0]}=${value}`))
    return multipleSelected.join('&')
  })
}

export const uniqueArrayElements = (array: string[]) => [...new Set(array)]

export const getUrl = ({
  currentSearchQueryParams,
  prefix,
  offset,
  calculatedOffset,
  urlFromProps,
}: {
  currentSearchQueryParams: string
  prefix: string
  offset: string[]
  calculatedOffset: string[]
  urlFromProps: string
}): string => {
  const offsetsQueryString =
    currentSearchQueryParams?.search('offset') > -1 || currentSearchQueryParams?.search('calculatedOffset') > -1
      ? ''
      : `${prefix}offset=${offset[0]}&${prefix}calculatedOffset=${calculatedOffset[0]}`

  if (currentSearchQueryParams) {
    return `${urlFromProps}?${currentSearchQueryParams}${offsetsQueryString ? '&' + offsetsQueryString : ''}`
  } else {
    return `${urlFromProps}${offsetsQueryString ? '?' + offsetsQueryString : ''}`
  }
}
export const updateOffsetStates = ({ loadMore, calculatedOffset, itemsCount, offsetState, setOffsetState, setCalculatedOffsetState }) => {
  if (!loadMore) return
  if (calculatedOffset) {
    setCalculatedOffsetState([`${calculatedOffset}`])
    setOffsetState([`${calculatedOffset}`])
  } else {
    const nextOffset = offsetState[0] == '0' ? [`${itemsCount}`] : [`${Number(offsetState[0]) + itemsCount}`]
    setCalculatedOffsetState(nextOffset)
    setOffsetState(nextOffset)
  }
}

export const clearAuthorModeCache = (prefix: string) => {
  if (!isAuthorMode()) return

  sessionStorage.removeItem(`${prefix}-filterFields`)
  sessionStorage.removeItem(`${prefix}-secondaryFilterFields`)
}